// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Clatter — the sheet and the tray, as HTML. Pure builders: they take state and return markup, and
// main.ts owns every listener. Nothing here measures the DOM, so a backgrounded peer paints exactly
// what a foregrounded one does (a layout read in a hidden tab returns zero and has silently broken
// a board in this fleet before).
//
// THE TAPPABLE UNIT IS A TRACK, NOT A CELL. Ladder, Tally and Run all place into a FORCED cell — the
// rules decide which — so making the whole row the target buys a 44px-tall hit area for free instead
// of asking a thumb to find a 30px box. Only the Weights need cell-level aim, and there the unit is
// the PAIR (78×44), which is also exactly the choice the rules ask for.

import { escapeHtml } from './dom';
import {
  LADDER_BONUS,
  RUN_BONUS,
  RUN_FLOOR,
  TALLY_BONUS,
  TALLY_DEPTH,
  TRACK_GLYPH,
  TRACK_NAME,
  T_LADDER,
  T_RUN,
  T_TALLY,
  T_WEIGHTS,
  WEIGHT_BONUS,
  B_FREE_LADDER,
  B_FREE_RUN,
  B_FREE_TALLY,
  B_FREE_WEIGHT,
  B_NONE,
  B_PTS,
  B_SHIM,
  ladderFilled,
  score,
  type Breakdown,
  type Sheet,
} from './rules';
import type { Mode } from './modes';

/** Pip layout as a 3×3 grid mask, so a die is nine spans and no images. */
const PIP_MASK: number[][] = [
  [],
  [4],
  [0, 8],
  [0, 4, 8],
  [0, 2, 6, 8],
  [0, 2, 4, 6, 8],
  [0, 2, 3, 5, 6, 8],
];

export interface DieOpts {
  action?: string;
  selected?: boolean;
  spilling?: boolean;
  disabled?: boolean;
  small?: boolean;
  /** The face it will actually be written as, once a shim is armed. */
  shifted?: number;
  label?: string;
}

export function dieHtml(face: number, o: DieOpts = {}): string {
  const f = Math.max(1, Math.min(6, face | 0));
  const shown = o.shifted !== undefined ? o.shifted : f;
  const pips = (PIP_MASK[shown] ?? [])
    .map((i) => `<i style="grid-area:${Math.floor(i / 3) + 1}/${(i % 3) + 1}"></i>`)
    .join('');
  const cls = [
    'die',
    o.small ? 'sm' : '',
    o.selected ? 'on' : '',
    o.spilling ? 'spilling' : '',
    o.shifted !== undefined && o.shifted !== f ? 'shifted' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const tag = o.action ? 'button' : 'span';
  const attrs = o.action ? ` type="button" data-die="${escapeHtml(o.action)}"${o.disabled ? ' disabled' : ''}` : '';
  const aria = o.label ? ` aria-label="${escapeHtml(o.label)}"` : ` aria-label="a ${shown}"`;
  return `<${tag} class="${cls}"${attrs}${aria}><span class="pips">${pips}</span></${tag}>`;
}

const bonusGlyph = (b: number): string => {
  if (b === B_NONE) return '';
  if (b >= B_PTS) return `+${b - B_PTS}`;
  if (b === B_SHIM) return '◇';
  if (b === B_FREE_LADDER) return '▲';
  if (b === B_FREE_TALLY) return '●';
  if (b === B_FREE_RUN) return '◆';
  if (b === B_FREE_WEIGHT) return '■';
  return '';
};

const bonusTitle = (b: number): string => {
  if (b === B_NONE) return '';
  if (b >= B_PTS) return `${b - B_PTS} points`;
  if (b === B_SHIM) return 'a shim';
  if (b === B_FREE_LADDER) return 'a free Ladder mark';
  if (b === B_FREE_TALLY) return 'a free Tally mark';
  if (b === B_FREE_RUN) return 'a free Run mark';
  if (b === B_FREE_WEIGHT) return 'a free Weights mark';
  return '';
};

const pip = (b: number): string => (b === B_NONE ? '' : `<em class="bpip" title="${escapeHtml(bonusTitle(b))}">${bonusGlyph(b)}</em>`);

export interface SheetHints {
  /** Tracks (and, for the Weights, pair slots) the selected die may legally go into. */
  live: Set<string>;
  /** True when this sheet belongs to the local player and they are choosing right now. */
  interactive: boolean;
  /** Cells written by the most recent action, as `${track}:${cell}` — flashed. */
  fresh: Set<string>;
  compact: boolean;
}

export const emptyHints = (): SheetHints => ({ live: new Set(), interactive: false, fresh: new Set(), compact: false });

const liveCls = (h: SheetHints, key: string): string => (h.interactive && h.live.has(key) ? ' live' : '');

function ladderHtml(s: Sheet, h: SheetHints, b: Breakdown): string {
  const k = ladderFilled(s);
  const cells = s.ladder
    .map((v, i) => {
      const fresh = h.fresh.has(`${T_LADDER}:${i}`) ? ' fresh' : '';
      const next = i === k ? ' next' : '';
      return `<span class="cell${v >= 0 ? ' done' : ''}${fresh}${next}">${v >= 0 ? v : ''}${pip(LADDER_BONUS[i])}</span>`;
    })
    .join('');
  return trackShell(T_LADDER, h, b.ladder, `<div class="cells ladder-cells">${cells}</div>`, 'each one higher than the last');
}

function tallyHtml(s: Sheet, h: SheetHints, b: Breakdown): string {
  const cols = s.tally
    .map((depth, c) => {
      const stack = Array.from({ length: TALLY_DEPTH }, (_, row) => {
        const level = TALLY_DEPTH - 1 - row;
        const filled = depth > level;
        const fresh = filled && h.fresh.has(`${T_TALLY}:${c}`) ? ' fresh' : '';
        return `<span class="box${filled ? ' done' : ''}${fresh}">${level === TALLY_DEPTH - 1 ? pip(TALLY_BONUS[c]) : ''}</span>`;
      }).join('');
      return `<span class="tcol"><span class="stack">${stack}</span>${dieHtml(c + 1, { small: true })}</span>`;
    })
    .join('');
  return trackShell(T_TALLY, h, b.tally, `<div class="cells tally-cells">${cols}</div>`, 'a die goes in its own column');
}

function runHtml(s: Sheet, h: SheetHints, b: Breakdown): string {
  const cells = RUN_FLOOR.map((floor, i) => {
    const done = s.run > i;
    const fresh = done && h.fresh.has(`${T_RUN}:${i}`) ? ' fresh' : '';
    const next = i === s.run ? ' next' : '';
    return `<span class="cell${done ? ' done' : ''}${fresh}${next}"><b>${floor}<span class="plus">+</span></b>${pip(RUN_BONUS[i])}</span>`;
  }).join('');
  return trackShell(T_RUN, h, b.run, `<div class="cells run-cells">${cells}</div>`, 'at least the printed number');
}

function weightsHtml(s: Sheet, h: SheetHints, b: Breakdown): string {
  const pairs = s.weights
    .map((w, p) => {
      const key = `${T_WEIGHTS}:${p}`;
      const balanced = w === 10;
      const open = w > 0 && w < 10;
      const left = balanced || open ? (balanced ? '✓' : String(w)) : '';
      const right = balanced ? '✓' : open ? String(7 - w) : '';
      const fresh = h.fresh.has(`${T_WEIGHTS}:${p * 2}`) || h.fresh.has(`${T_WEIGHTS}:${p * 2 + 1}`) ? ' fresh' : '';
      const tag = h.interactive && h.live.has(key) ? 'button' : 'span';
      const attrs = tag === 'button' ? ` type="button" data-place="${T_WEIGHTS}:${p}"` : '';
      return `<${tag} class="pair${balanced ? ' done' : open ? ' open' : ''}${fresh}${liveCls(h, key)}"${attrs} aria-label="${
        balanced ? 'balanced pair' : open ? `pair needs a ${7 - w}` : 'empty pair'
      }"><span class="pan">${left}</span><em>=7</em><span class="pan${open ? ' want' : ''}">${right}</span>${pip(WEIGHT_BONUS[p])}</${tag}>`;
    })
    .join('');
  return trackShell(T_WEIGHTS, h, b.weights, `<div class="cells weight-cells">${pairs}</div>`, 'two dice that make seven', true);
}

function trackShell(track: number, h: SheetHints, points: number, inner: string, rule: string, pairLevel = false): string {
  const key = String(track);
  const live = h.interactive && h.live.has(key);
  const tag = live && !pairLevel ? 'button' : 'div';
  const attrs = tag === 'button' ? ` type="button" data-place="${track}"` : '';
  return `<${tag} class="track t${track}${live ? ' live' : ''}"${attrs}>
      <div class="thead"><span class="tg">${TRACK_GLYPH[track]}</span><span class="tn">${TRACK_NAME[track]}</span><span class="trule">${escapeHtml(
        rule,
      )}</span><span class="tsc">${points}</span></div>
      ${inner}
    </${tag}>`;
}

/** One player's sheet. */
export function sheetHtml(s: Sheet, mode: Mode, h: SheetHints): string {
  const b = score(s, mode);
  const dropped = mode.scoreBest > 0 && mode.scoreBest < 4;
  return `<div class="sheet${h.compact ? ' compact' : ''}${dropped ? ' best-only' : ''}">
      ${ladderHtml(s, h, b)}
      ${tallyHtml(s, h, b)}
      ${runHtml(s, h, b)}
      ${weightsHtml(s, h, b)}
    </div>`;
}

/** The shim wallet, shown next to the tray. */
export function shimsHtml(s: Sheet, armed: number, usable: boolean): string {
  const pips = Array.from({ length: 3 }, (_, i) => `<i class="${i < s.shims ? 'on' : ''}"></i>`).join('');
  const btn = (dir: number, sign: string): string =>
    `<button type="button" class="shimbtn${armed === dir ? ' on' : ''}" data-shim="${dir}"${
      usable && s.shims > 0 ? '' : ' disabled'
    } aria-label="nudge the die ${sign}1" aria-pressed="${armed === dir}">${sign}1</button>`;
  return `<div class="shims"><span class="shim-pips" aria-label="${s.shims} shims">${pips}</span>${btn(2, '−')}${btn(1, '+')}</div>`;
}

/** The compact scoreboard of everyone else. */
export function scoreStripHtml(sheets: Sheet[], mode: Mode, names: string[], mySeat: number | null, waiting: number[]): string {
  const rows = sheets
    .map((sh, seat) => {
      const b = score(sh, mode);
      const me = seat === mySeat;
      // No spinner on your own chip: you are not waiting for yourself.
      const wait = waiting.includes(seat) && seat !== mySeat;
      return `<button type="button" class="pchip s${seat}${me ? ' me' : ''}${wait ? ' waiting' : ''}" data-peek="${seat}" aria-label="${escapeHtml(
        names[seat] ?? `Player ${seat + 1}`,
      )}, ${b.total} points — tap to see their sheet"><span class="pn">${escapeHtml(names[seat] ?? `P${seat + 1}`)}</span><span class="pv">${
        b.total
      }</span>${wait ? '<span class="spinner sm"></span>' : ''}</button>`;
    })
    .join('');
  return `<div class="strip">${rows}</div>`;
}
